import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useFetchUrlMetadataQuery } from "services/bffService";

const isValidUrl = (text) => {
  if (!text || text.trim() === "") return false;
  try {
    const url = new URL(text.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (e) {
    try {
      new URL(`https://${text.trim()}`);
      return true;
    } catch (e2) {
      return false;
    }
  }
};

const normalizeUrlForStorage = (text) => {
  let s = text.trim();
  // Add https:// if no protocol is present
  if (!/^https?:\/\//i.test(s)) {
    s = `https://${s}`;
  }
  s = s.replace(/\/$/, "");
  return s;
};

const NewLinkDialog = ({ open, onClose, onSave, initialValue }) => {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [urlError, setUrlError] = useState(null);
  const [requestUrl, setRequestUrl] = useState(null);

  useEffect(() => {
    setUrl(initialValue?.url ?? "");
    setTitle(initialValue?.title ?? "");
    setUrlError(null);
    setRequestUrl(null);
  }, [initialValue, open]);

  const { data } = useFetchUrlMetadataQuery(
    { url: requestUrl },
    { skip: !requestUrl }
  );
  const metaData = requestUrl ? data : null;

  useEffect(() => {
    if (!metaData || !open) return;
    if (!initialValue?.title && title === "" && metaData.title) {
      setTitle(metaData.title);
    }
  }, [metaData, initialValue, title, open]);

  const handleSave = () => {
    if (!isValidUrl(url)) {
      setUrlError("Please enter a valid URL");
      return;
    }
    setUrlError(null);
    const normalized = normalizeUrlForStorage(metaData?.urlResolved || url);
    onSave({ url: normalized, title: title.trim() });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {initialValue ? "Edit Link" : "New Link"}
        <IconButton onClick={onClose} size={"small"}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            autoFocus
            label={"URL"}
            variant={"filled"}
            value={url}
            onChange={(e) => {
              const newUrl = e.target.value;
              setUrl(newUrl);
              if (urlError != null) {
                setUrlError(null);
              }
              if (isValidUrl(newUrl)) {
                setRequestUrl(newUrl);
              }
            }}
            placeholder={"https://example.com"}
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={urlError != null}
            helperText={urlError ?? ""}
          />
          <TextField
            label={"Title (optional)"}
            variant={"filled"}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          variant={"contained"}
          disableElevation
          sx={{ width: "100px" }}
        >
          {initialValue ? "Save" : "Add"}
        </Button>
        <Button
          onClick={onClose}
          variant={"contained"}
          color={"secondary"}
          sx={{ width: "100px" }}
        >
          {"Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewLinkDialog;
